import { useState } from "react"
import Modal from "./Modal"
import Icon from "../../components/Icon"
import { getApiErrorMessage } from "../../utils/errorMessage"

function formatDateTimeLocalInput(value) {
  if (!value) return ""
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  const hours = String(date.getHours()).padStart(2, "0")
  const minutes = String(date.getMinutes()).padStart(2, "0")
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

function EditInvoiceModal({ invoice, onClose, onSave }) {
  const [form, setForm] = useState({
    amount: String(Number(invoice?.amount || 0) || ""),
    description: String(invoice?.description || ""),
    dueDate: formatDateTimeLocalInput(invoice?.dueDate),
    date: formatDateTimeLocalInput(invoice?.date || invoice?.createdAt),
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const canSubmit =
    Number(form.amount) > 0 &&
    String(form.description || "").trim().length > 0 &&
    String(form.date || "").trim().length > 0

  const submit = async (event) => {
    event.preventDefault()
    if (saving || !canSubmit) return

    try {
      setSaving(true)
      setError("")
      await onSave({
        amount: Number(form.amount),
        description: String(form.description || "").trim(),
        dueDate: form.dueDate || null,
        date: form.date || null,
      })
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to update invoice."))
      setSaving(false)
    }
  }

  return (
    <Modal
      title="Edit utility invoice"
      subtitle={invoice?.customerName || invoice?.providerName || invoice?.description || "Update bill details"}
      icon="pencil-square"
      onClose={onClose}
      footer={
        <button
          type="submit"
          form="ut-edit-invoice-form"
          disabled={saving || !canSubmit}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          <Icon name="save" size="0.8rem" />
          {saving ? "Saving..." : "Save changes"}
        </button>
      }
    >
      <form id="ut-edit-invoice-form" onSubmit={submit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">Amount</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={form.amount}
            onChange={(event) => setForm((current) => ({ ...current, amount: event.target.value }))}
            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
            required
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">Invoice date</label>
          <input
            type="datetime-local"
            value={form.date}
            onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))}
            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
            required
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">Due date</label>
          <input
            type="datetime-local"
            value={form.dueDate}
            onChange={(event) => setForm((current) => ({ ...current, dueDate: event.target.value }))}
            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-semibold text-slate-700">Description</label>
          <textarea
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            rows="4"
            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
            required
          />
        </div>

        {error && <p className="text-sm font-medium text-red-600">{error}</p>}
      </form>
    </Modal>
  )
}

export default EditInvoiceModal
